import { classList } from '@findigs/helpers/src/helpers/class-list';
import { Button } from '@findigs/ui-kit/src/components/button';
import { FormMultipleChoice } from '@findigs/ui-kit/src/components/form-multiple-choice';
import { tintsNeutral90 } from '@findigs/ui-kit/src/css/colors/_colors.module.scss';
import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { PRETTY_PET_ANIMAL_STATUS } from '../consts/pretty-pet-animal-status';
import { PRETTY_PET_SPECIES } from '../consts/pretty-pet-species';
import type { UseRemoveProfileEmbeddedModalState } from '../hooks/use-remove-profile-embedded-modal';
import ConfirmModalLayout from '../layouts/confirm-modal-layout';
import type { ModalProps } from '../state/modal';
import WarningIcon from '../images/modals/warning-icon.inline.svg';

export interface RemoveProfileEmbeddedModalProps extends ModalProps {
  options: UseRemoveProfileEmbeddedModalState['options'];
}

const StyledConfirmModalLayout = styled(ConfirmModalLayout)`
  .text-muted {
    color: ${tintsNeutral90};
  }

  /** 768px - tablet **/
  @media (min-width: 48rem) {
    max-width: 27.25rem;
  }
`;

const RemoveProfileEmbeddedModal = ({
  options: { onConfirm, pets },
  hide,
}: RemoveProfileEmbeddedModalProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [removingAllPets, setRemovingAllPets] = useState(false);

  const _onConfirm = useCallback(() => {
    if (onConfirm) {
      const removing = new FormData(formRef.current ?? undefined)
        .getAll('pets')
        .map(String);

      if (!removingAllPets && removing.length === pets.length) {
        setRemovingAllPets(true);
      } else {
        hide();
        onConfirm(removing);
      }
    } else {
      hide();
    }
  }, [hide, onConfirm, pets, removingAllPets]);

  const [empty, setEmpty] = useState(true);

  const Confirm = (
    <Button
      type="button"
      variant="FilledStandard"
      onClick={_onConfirm}
      disabled={empty}
      //key is here to force a re-render resetting hover states
      key={`btn-confirm${removingAllPets ? '' : '-selection'}`}
    >
      {removingAllPets ? 'Confirm' : 'Confirm selection'}
    </Button>
  );
  const Cancel = (
    <Button type="button" variant="Tonal" onClick={hide} className="cancel">
      Cancel
    </Button>
  );

  return (
    <StyledConfirmModalLayout
      onDismiss={hide}
      icon={removingAllPets ? <WarningIcon aria-hidden={true} /> : undefined}
      actions={removingAllPets ? [Confirm, Cancel] : [Confirm]}
    >
      <h2 className="text-t-sm">
        {removingAllPets
          ? 'Remove all profiles?'
          : 'Select the animal profile you’d like to remove'}
      </h2>
      {removingAllPets && (
        <p className="text-b-md-regular text-muted pt-16">
          You’re about to remove all animal profiles, which will cancel animal
          verification. To add an animal again, contact your property manager.
        </p>
      )}

      <form
        ref={formRef}
        className={classList('pt-32', { 'd-none': removingAllPets })}
      >
        <FormMultipleChoice
          name="pets"
          checkboxes
          borderless
          onChange={() =>
            setEmpty(
              new FormData(formRef.current ?? undefined).getAll('pets')
                ?.length === 0
            )
          }
          options={pets.map((pet) => {
            let species: string | null = null;
            if (pet.species === 'Other' && pet.breed) {
              species = pet.breed;
            } else if (pet.species) {
              species = PRETTY_PET_SPECIES[pet.species].text;
            }

            const animalStatus = pet.animal_status
              ? PRETTY_PET_ANIMAL_STATUS[pet.animal_status].text
              : null;

            return {
              value: pet.id,
              render: () => (
                <div>
                  <p className="text-b-sm-medium">
                    {pet.pet_name ?? 'New animal'}
                  </p>
                  <p className="text-b-sm-regular">
                    {[species, animalStatus].filter(Boolean).join(' - ')}
                  </p>
                </div>
              ),
            };
          })}
        />
      </form>
    </StyledConfirmModalLayout>
  );
};

export default RemoveProfileEmbeddedModal;
